import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import {
  fetchMappingTemplates,
  latestMappingTemplates,
  type MappingTemplate,
} from "@/lib/portalData";

import type { PlannedRecord } from "./createRecordTemplates.helpers";

type Props = {
  /** The connector's TARGET type (e.g. "Xero") — only templates for this target
   *  are offered. */
  targetType: string | null;
  value: PlannedRecord[];
  onChange: (records: PlannedRecord[]) => void;
  /** The loaded (target-filtered) templates, lifted so the form can validate
   *  planned records against their binding definitions before submit. */
  onTemplatesLoaded?: (templates: MappingTemplate[]) => void;
};

/**
 * Optional "Record types" step in the connector-create wizard (WS1 P3 PR4b):
 * pick one or more mapping templates and bind their source columns, so the new
 * connector's mappings are stamped at create time — the "2-minute form" that
 * fully kills friction #3's create-tax. Bindings are free text here (the new
 * connector can't be introspected yet); refine with the editor's column
 * dropdowns after create.
 */
export function CreateRecordTemplates({
  targetType,
  value,
  onChange,
  onTemplatesLoaded,
}: Props) {
  const { token } = useAuth();
  const [templates, setTemplates] = useState<MappingTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const nextId = useState(() => ({ n: -1 }))[0];

  useEffect(() => {
    if (!token || !targetType) return;
    let cancelled = false; // a slow response for an old target must not win
    setLoading(true);
    fetchMappingTemplates(token)
      .then((all) => {
        if (cancelled) return;
        // Only the latest version of each template is offered at create time —
        // every version shares a record_name, so they'd render as twin buttons.
        const forTarget = latestMappingTemplates(
          all.filter((t) => t.target_type.toLowerCase() === targetType.toLowerCase()),
        );
        setTemplates(forTarget);
        onTemplatesLoaded?.(forTarget);
      })
      .catch(() => {
        if (!cancelled) {
          setTemplates([]);
          onTemplatesLoaded?.([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // onTemplatesLoaded intentionally excluded — parent passes a stable setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, targetType]);

  const addRecord = (t: MappingTemplate) => {
    const id = nextId.n;
    nextId.n -= 1;
    onChange([
      ...value,
      {
        id,
        templateKey: t.key,
        version: t.version,
        recordName: t.record_name,
        recordSource: "",
        bindings: {},
      },
    ]);
  };

  const removeRecord = (id: number) => onChange(value.filter((r) => r.id !== id));
  const patch = (id: number, change: Partial<PlannedRecord>) =>
    onChange(value.map((r) => (r.id === id ? { ...r, ...change } : r)));
  const setBinding = (id: number, slot: string, v: string) =>
    onChange(
      value.map((r) =>
        r.id === id ? { ...r, bindings: { ...r.bindings, [slot]: v } } : r,
      ),
    );

  const templateOf = (r: PlannedRecord) =>
    templates.find((t) => t.key === r.templateKey && t.version === r.version) ?? null;

  if (!targetType) return null;
  if (loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
      </p>
    );
  }
  if (templates.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No mapping templates for {targetType} — you can add mappings from the
        connector after it's created.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="create-record-templates">
      <p className="text-xs text-muted-foreground">
        Optional — stamp standard mappings now so the connector is ready to sync.
        You can add or edit these later.
      </p>

      <div className="flex flex-wrap gap-1.5">
        {templates.map((t) => (
          <Button
            key={`${t.key}@${t.version}`}
            type="button"
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            // Each template stamps ONE record (by its fixed record_name), and the
            // replace is declarative — adding it twice would just let the last win.
            disabled={value.some((r) => r.templateKey === t.key && r.version === t.version)}
            onClick={() => addRecord(t)}
            data-testid={`add-template-${t.key}`}
          >
            <Plus className="h-3 w-3" />
            {t.record_name}
          </Button>
        ))}
      </div>

      {value.map((r) => {
        const t = templateOf(r);
        return (
          <div key={r.id} className="rounded-md border border-slate-200 p-2.5">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-medium">{r.recordName}</span>
              <span className="text-[11px] text-muted-foreground">
                {r.templateKey} v{r.version}
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="ml-auto h-6 w-6 p-0 text-muted-foreground"
                onClick={() => removeRecord(r.id)}
                aria-label={`Remove ${r.recordName}`}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>

            <div className="mt-2 space-y-1.5">
              <div className="space-y-0.5">
                <label
                  className="text-[11px] font-medium text-slate-700"
                  htmlFor={`src-${r.id}`}
                >
                  Source view / table <span className="text-destructive">*</span>
                </label>
                <Input
                  id={`src-${r.id}`}
                  aria-label={`Source for ${r.recordName}`}
                  className="h-7 text-xs"
                  value={r.recordSource}
                  maxLength={50} // B2CClientConnectorRecord.Source is varchar(50)
                  placeholder="e.g. vw_Invoices"
                  onChange={(e) => patch(r.id, { recordSource: e.target.value })}
                />
              </div>

              {t?.bindings.map((b) => (
                <div key={b.slot} className="space-y-0.5">
                  <label
                    className="text-[11px] font-medium text-slate-700"
                    htmlFor={`bind-${r.id}-${b.slot}`}
                  >
                    {b.label}
                    {b.required && <span className="text-destructive"> *</span>}
                    {b.kind !== "column" && (
                      <span className="ml-1 text-muted-foreground">(literal)</span>
                    )}
                  </label>
                  <Input
                    id={`bind-${r.id}-${b.slot}`}
                    aria-label={`${r.recordName} — ${b.label}`}
                    className="h-7 text-xs"
                    value={r.bindings[b.slot] ?? ""}
                    placeholder={b.help_text || (b.kind === "column" ? "source column" : "value")}
                    onChange={(e) => setBinding(r.id, b.slot, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
